import type { ApiResponse } from '@personaltota/types';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001/v1';

// ─── Token storage ─────────────────────────────────────────────────────────

export const tokenStore = {
  get: (): string | null =>
    typeof window !== 'undefined' ? localStorage.getItem('pt-token') : null,
  set: (token: string): void => localStorage.setItem('pt-token', token),
  clear: (): void => {
    localStorage.removeItem('pt-token');
    localStorage.removeItem('pt-refresh');
  },
  getRefresh: (): string | null =>
    typeof window !== 'undefined' ? localStorage.getItem('pt-refresh') : null,
  setRefresh: (token: string): void => localStorage.setItem('pt-refresh', token),
};

// ─── Core fetch wrapper ───────────────────────────────────────────────────

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  auth?: boolean;
  headers?: Record<string, string>;
  isFormData?: boolean;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public details: string[],
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, headers = {}, isFormData = false } = options;

  const reqHeaders: Record<string, string> = {
    // Skip Content-Type for FormData — browser sets it with the multipart boundary
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...headers,
  };

  if (auth) {
    const token = tokenStore.get();
    if (token) reqHeaders['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: reqHeaders,
    body: isFormData
      ? (body as FormData)
      : body !== undefined
        ? JSON.stringify(body)
        : undefined,
  });

  const json: ApiResponse<T> = await res.json();

  if (!json.success) {
    throw new ApiError(
      res.status,
      json.error?.code ?? 'ERROR',
      json.error?.details ?? [],
      json.message,
    );
  }

  return json.data as T;
}

// ─── Auth ─────────────────────────────────────────────────────────────────

export const authApi = {
  register: (body: { fullName: string; email: string; password: string; phone?: string; timezone: string }) =>
    request<{ userId: string; email: string; message: string }>('/auth/register', {
      method: 'POST',
      body,
      auth: false,
    }),

  login: (body: { email: string; password: string }) =>
    request<{
      accessToken: string;
      refreshToken: string;
      expiresAt: string;
      user: { userId: string; email: string; accountType: string };
    }>('/auth/login', { method: 'POST', body, auth: false }),

  refresh: (refreshToken: string) =>
    request<{ accessToken: string; expiresAt: string }>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
      auth: false,
    }),

  logout: () =>
    request<null>('/auth/logout', {
      method: 'POST',
      body: { refreshToken: tokenStore.getRefresh() },
    }),

  forgotPassword: (email: string) =>
    request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: { email },
      auth: false,
    }),

  resetPassword: (token: string, newPassword: string) =>
    request<null>('/auth/reset-password', {
      method: 'POST',
      body: { token, newPassword },
      auth: false,
    }),
};

// ─── Users ────────────────────────────────────────────────────────────────

export const usersApi = {
  me: () =>
    request<{
      userId: string;
      email: string;
      accountType: string;
      isVerified: boolean;
      isAdmin: boolean;
      profile: { fullName: string; companyName: string; phone: string; timezone: string };
    }>('/users/me'),

  updateMe: (body: { fullName?: string; companyName?: string; phone?: string; timezone?: string }) =>
    request<{ fullName: string; companyName: string; phone: string; timezone: string }>(
      '/users/me',
      { method: 'PUT', body },
    ),

  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    request<null>('/users/me/password', { method: 'PUT', body }),
};

// ─── Voices ───────────────────────────────────────────────────────────────

export type VoiceItem = {
  id: string;
  name: string;
  type: 'PLATFORM' | 'UPLOADED' | 'CLONED';
  language: string;
  gender: 'MALE' | 'FEMALE' | 'NEUTRAL' | null;
  scope: 'SHARED' | 'PRIVATE';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  ownerId: string | null;
  sampleFileUrl: string | null;
  createdAt: string;
};

export type VoiceListResponse = {
  items: VoiceItem[];
  pagination: { page: number; perPage: number; total: number; totalPages: number };
};

export const voicesApi = {
  list: (params?: { type?: string; language?: string; status?: string; page?: number; perPage?: number }) => {
    const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)])).toString() : '';
    return request<VoiceListResponse>(`/voices${qs}`);
  },

  get: (voiceId: string) =>
    request<VoiceItem & { sampleFileUrl: string | null }>(`/voices/${voiceId}`),

  upload: (formData: FormData) =>
    request<{ voiceId: string; name: string; status: string; message: string }>(
      '/voices/upload',
      { method: 'POST', body: formData, isFormData: true },
    ),

  clone: (body: { sourceVoiceId: string; name: string; language: string }) =>
    request<{ voiceId: string; name: string; type: string; status: string; cloningJob: { jobId: string; status: string } }>(
      '/voices/clone',
      { method: 'POST', body },
    ),

  delete: (voiceId: string) =>
    request<null>(`/voices/${voiceId}`, { method: 'DELETE' }),
};

// ─── Templates ───────────────────────────────────────────────────────────────

export type TemplateStatus =
  | 'DRAFT'
  | 'SCRIPT_SUBMITTED'
  | 'SCRIPT_UNDER_REVIEW'
  | 'SCRIPT_APPROVED'
  | 'SCRIPT_REJECTED'
  | 'TTS_CHUNKING_PENDING'
  | 'TTS_CHUNKING_DONE'
  | 'VOICE_APPROVED'
  | 'COMMITTED'
  | 'ARCHIVED';

export type CampaignType =
  | 'SIMPLE'
  | 'PERSONALIZED_FIRST_NAME'
  | 'PERSONALIZED_FULL_NAME'
  | 'CUSTOM'
  | 'AUDIO';

export type TemplateItem = {
  id: string;
  name: string;
  type: CampaignType;
  language: string;
  status: TemplateStatus;
  folderId: string | null;
  testCallsUsed: number;
  createdAt: string;
  updatedAt: string;
  voice: { id: string; name: string } | null;
  folder: { id: string; name: string } | null;
};

export type TemplateDetail = TemplateItem & {
  displayScript: string | null;
  variables: string[] | null;
  rejectionReason: string | null;
  audioFileUrl: string | null;
};

export type TemplateListResponse = {
  items: TemplateItem[];
  pagination: { page: number; perPage: number; total: number; totalPages: number };
};

export type TemplateFolder = { id: string; name: string; createdAt: string };

function buildQs(params?: Record<string, string | number | undefined | null>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v != null) as [string, string | number][];
  if (!entries.length) return '';
  return '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

export const templatesApi = {
  list: (params?: { status?: string; folderId?: string; type?: string; page?: number; perPage?: number }) =>
    request<TemplateListResponse>(`/templates${buildQs(params)}`),

  get: (id: string) =>
    request<TemplateDetail>(`/templates/${id}`),

  create: (body: { name: string; type: CampaignType; language: string; voiceId?: string; displayScript?: string; folderId?: string }) =>
    request<TemplateDetail>('/templates', { method: 'POST', body }),

  uploadAudio: (id: string, formData: FormData) =>
    request<{ audioFileUrl: string }>(`/templates/${id}/audio`, { method: 'POST', body: formData, isFormData: true }),

  update: (id: string, body: { name?: string; type?: CampaignType; language?: string; voiceId?: string; displayScript?: string; folderId?: string | null }) =>
    request<TemplateDetail>(`/templates/${id}`, { method: 'PUT', body }),

  submit: (id: string) =>
    request<{ id: string; status: TemplateStatus; updatedAt: string }>(`/templates/${id}/submit`, { method: 'POST' }),

  archive: (id: string) =>
    request<{ id: string; status: TemplateStatus; updatedAt: string }>(`/templates/${id}/archive`, { method: 'POST' }),

  commit: (id: string) =>
    request<{ id: string; status: TemplateStatus; committedAt: string; updatedAt: string }>(`/templates/${id}/commit`, { method: 'POST' }),

  testCall: (id: string, phoneNumbers: string[]) =>
    request<{ testRunId: string; recipientCount: number; remaining: number; message: string }>(
      `/templates/${id}/test-call`,
      { method: 'POST', body: { phoneNumbers } },
    ),
};

export const foldersApi = {
  list: () => request<TemplateFolder[]>('/template-folders'),
  create: (name: string) => request<TemplateFolder>('/template-folders', { method: 'POST', body: { name } }),
  delete: (id: string) => request<null>(`/template-folders/${id}`, { method: 'DELETE' }),
};

// ─── Admin ─────────────────────────────────────────────────────────────────

export type AdminVoice = {
  id: string;
  name: string;
  language: string;
  type: string;
  status: string;
  rejectionReason: string | null;
  createdAt: string;
  sampleFilePath: string | null;
  owner: { id: string; email: string; profile: { fullName: string } | null } | null;
};

export type AdminTemplate = {
  id: string;
  name: string;
  type: string;
  language: string;
  status: string;
  displayScript: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
  user: { id: string; email: string; profile: { fullName: string } | null };
  voice: { id: string; name: string } | null;
};

export const adminApi = {
  listPendingVoices: () => request<AdminVoice[]>('/admin/voices/pending'),
  approveVoice: (id: string) => request<AdminVoice>(`/admin/voices/${id}/approve`, { method: 'POST' }),
  rejectVoice: (id: string, reason: string) =>
    request<AdminVoice>(`/admin/voices/${id}/reject`, { method: 'POST', body: { reason } }),
  listPendingTemplates: () => request<AdminTemplate[]>('/admin/templates/pending'),
  approveTemplate: (id: string) => request<AdminTemplate>(`/admin/templates/${id}/approve`, { method: 'POST' }),
  rejectTemplate: (id: string, reason: string) =>
    request<AdminTemplate>(`/admin/templates/${id}/reject`, { method: 'POST', body: { reason } }),
};

export default request;
